import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { catchError, debounceTime, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { TSP } from '../../../core/models';
import { CityService } from '../../../core/services/city/city.service';

export interface CityReplaceDialogData {
  city: TSP.Internal.Dictionary.City.Details;
}

export interface CityReplaceDialogResult {
  targetCityId: number;
}

@Component({
  selector: 'app-city-replace-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: './city-replace-dialog.html',
  styleUrl: './city-replace-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CityReplaceDialog {
  private readonly dialogRef = inject(MatDialogRef<CityReplaceDialog>);
  private readonly cityService = inject(CityService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly data = inject<CityReplaceDialogData>(MAT_DIALOG_DATA);

  public searchControl = new FormControl<string>('');
  public targetControl = new FormControl<number | null>(null, [Validators.required]);

  public readonly isLoading = signal(true);

  private static readonly PAGE_SIZE = 50;

  public readonly filteredCities$ = this.searchControl.valueChanges.pipe(
    startWith(''),
    distinctUntilChanged(),
    tap(() => this.isLoading.set(true)),
    debounceTime(300),
    switchMap((search) =>
      this.cityService
        .getCitiesList({
          search: search || undefined,
          size: CityReplaceDialog.PAGE_SIZE,
        })
        .pipe(
          map((response) => response.items.filter((c) => c.id !== this.data.city.id)),
          catchError(() => of<TSP.Internal.Dictionary.City.Details[]>([])),
        ),
    ),
    tap(() => this.isLoading.set(false)),
    takeUntilDestroyed(this.destroyRef),
  );

  public get sourceCity(): string {
    const city = this.data.city;
    return `${city.enName} / ${city.ukName}`;
  }

  public cityLabel(city: TSP.Internal.Dictionary.City.Details): string {
    return `${city.enName} / ${city.ukName} (${city.countryName}${city.regionName ? `, ${city.regionName}` : ''})`;
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }

  public onConfirm(): void {
    if (this.targetControl.invalid || !this.targetControl.value) {
      this.targetControl.markAsTouched();
      return;
    }
    this.dialogRef.close({ targetCityId: this.targetControl.value } satisfies CityReplaceDialogResult);
  }
}
